import { HttpErrorResponse, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { EntityType } from "@models/common/entity-type.enum";
import { ExternalEntity } from "@models/integrations/external-entity";
import { ExternalSourceType } from "@models/integrations/external-source-type.enum";
import { ShortEntityIdentityInfo } from "@models/integrations/short-entity-identity-info";
import { SyncIdentitiesModel } from "@models/integrations/sync-identities-model";
import BaseApiService from "@shared/services/base/base-api.service";
import { ToastrService } from "ngx-toastr";
import { map, Observable } from "rxjs";

@Injectable({ providedIn: 'root' })
export class EntityIdentityApiService extends BaseApiService {
  #toastr = inject(ToastrService);

  getConnectedProviders(): Observable<ExternalSourceType[]> {
    return this.api.get<ExternalSourceType[]>(`${ this.apiUrl }/identities/connected`);
  }

  isXpmConnected(): Observable<boolean> {
    return this.getConnectedProviders()
      .pipe(map((sourceTypes) => sourceTypes.includes(ExternalSourceType.Xpm)));
  }

  refresh(entityId: string, externalEntityId: string, sourceType: ExternalSourceType) {
    let params = new HttpParams();
    params = params.append('externalEntityId', externalEntityId);
    params = params.append('sourceType', sourceType);

    return this.api.get<void>(`${ this.apiUrl }/identities/${ entityId }/refresh`, { params: params });
  }

  unlinkEntity(entityId: string, externalEntityId: string) {
    let params = new HttpParams();
    params = params.append('externalEntityId', externalEntityId);

    return this.api.post<void>(`${ this.apiUrl }/identities/${ entityId }/unlink`, null, { params: params });
  }


  linkEntity(entityId: string, externalEntityId: string, entityType: EntityType) {
    let params = new HttpParams();
    params = params.append('externalEntityId', externalEntityId);
    params = params.append('entityType', entityType);

    return this.api.post<void>(`${ this.apiUrl }/identities/${ entityId }/link`, null, { params: params });
  }

  list(sourceType: ExternalSourceType, entityType: EntityType[] | null): Observable<ExternalEntity[]> {
    let params = new HttpParams();
    if (entityType != null && entityType.length > 0) {
      entityType.forEach(et => {
        params = params.append('entityType', et.toString());
      });
    }

    return this.api.get<ExternalEntity[]>(`${ this.apiUrl }/identities/${ sourceType }/list`, { params: params });
  }

  listPreview(sourceType: ExternalSourceType, entityType: EntityType, query: string | null): Observable<ShortEntityIdentityInfo[]> {
    let params = new HttpParams();
    params = params.append('entityType', EntityType[entityType]);
    params = params.append('sourceType', ExternalSourceType[sourceType]);

    if (query) {
      params = params.append('query', query);
    }

    return this.api.get<ShortEntityIdentityInfo[]>(`${ this.apiUrl }/identities/info/list/preview`, { params: params });
  }

  sync(sourceType: ExternalSourceType, model: SyncIdentitiesModel) {
    return this.api.post<void>(`${ this.apiUrl }/identities/${ sourceType }/sync`, model);
  }

  checkSyncError(error: unknown) {
    if (error instanceof HttpErrorResponse) {
      const errorMessage = (error.error as { message?: string })?.message;
      if (errorMessage?.includes('SynchronizationInProgressException')) {
        this.#toastr.warning('Synchronization in progress. Please try again later.');
        return true;
      }
    }
    return false;
  }
}
